/**
 * Modal para crear/editar Club
 */

import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import {
  X,
  Loader2,
  Upload,
  Trash2,
  ImageIcon,
  Search,
  CheckCircle,
} from "lucide-react";
import {
  useCreateClub,
  useUpdateClub,
  useChurches,
  useDistricts,
  useClubs,
} from "@/hooks/useOrganization";
import {
  uploadClubLogo,
  validateImageFile,
  deleteImage,
} from "@/lib/services/storage";
import { toast } from "react-hot-toast";
import type { Club, ClubType, SectionConfig } from "@/types";

const clubSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  churchId: z.string().optional(),
  sectionAventureros: z.boolean().default(false),
  sectionConquistadores: z.boolean().default(true),
  sectionGuiasMayores: z.boolean().default(false),
  meetingDay: z.string().optional(),
  meetingTime: z.string().optional(),
  contactEmail: z.string().email("Email inválido").optional().or(z.literal("")),
  contactPhone: z.string().optional(),
});

type ClubForm = z.infer<typeof clubSchema>;

interface ClubModalProps {
  isOpen: boolean;
  onClose: () => void;
  club?: Club | null;
  churchId?: string;
}

export default function ClubModal({
  isOpen,
  onClose,
  club,
  churchId,
}: ClubModalProps) {
  const isEditing = !!club;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    club?.logoUrl || null,
  );
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);

  const createMutation = useCreateClub();
  const updateMutation = useUpdateClub();
  const { data: allChurches = [] } = useChurches();
  const { data: districts = [] } = useDistricts();

  // F11: club fusión (unión temporal de 2+ clubes para un evento)
  const [isFusion, setIsFusion] = useState<boolean>(!!club?.isFusion);
  const [fusionOf, setFusionOf] = useState<string[]>(club?.fusionOf || []);
  const { data: allClubs = [] } = useClubs();
  const fusionCandidates = allClubs.filter(
    (c) => c.id !== club?.id && c.status === "active" && !c.isFusion,
  );

  // Filtrar iglesias por la asociación del club (si tiene)
  const associationId = club?.associationId;
  const availableChurches = associationId
    ? allChurches.filter((c) => c.associationId === associationId)
    : allChurches;

  const districtNameById = new Map(
    districts.map((d) => [d.id, d.name] as const),
  );
  const getDistrictLabel = (c: {
    districtName?: string;
    districtId?: string;
  }): string | null => {
    if (c.districtName) return c.districtName;
    if (c.districtId) return districtNameById.get(c.districtId) ?? null;
    return null;
  };

  // Estado del buscador de iglesia
  const [churchSearch, setChurchSearch] = useState("");
  const [selectedChurchId, setSelectedChurchId] = useState<string>(
    club?.churchId || churchId || "",
  );
  const selectedChurch = availableChurches.find(
    (c) => c.id === selectedChurchId,
  );

  // Inicializa la selección SOLO cuando se abre el modal o cambia el club editado.
  // No depender de `allChurches` para no pisar la selección del usuario en refetches.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedChurchId(club?.churchId || churchId || "");
    setChurchSearch("");
  }, [isOpen, club?.id, churchId]);

  // Hidrata el texto del buscador cuando ya tenemos las iglesias y hay una seleccionada
  // pero el input está vacío (sin tocar lo que el usuario haya escrito).
  useEffect(() => {
    if (!isOpen || !selectedChurchId || churchSearch) return;
    const found = allChurches.find((c) => c.id === selectedChurchId);
    if (found) setChurchSearch(found.name);
    // churchSearch fuera de deps a propósito para evitar pisar al usuario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedChurchId, allChurches]);

  // Reset logo state when modal opens/closes or club changes
  useEffect(() => {
    if (isOpen) {
      setLogoPreview(club?.logoUrl || null);
      setLogoFile(null);
      setRemoveLogo(false);
      setIsFusion(!!club?.isFusion);
      setFusionOf(club?.fusionOf || []);
    }
  }, [isOpen, club]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ClubForm>({
    resolver: zodResolver(clubSchema),
    defaultValues: {
      name: club?.name || "",
      churchId: club?.churchId || churchId || "",
      sectionAventureros:
        club?.sections?.aventureros?.active ??
        (club?.type === "aventureros" || false),
      sectionConquistadores:
        club?.sections?.conquistadores?.active ??
        (club?.type === "conquistadores" || !club),
      sectionGuiasMayores:
        club?.sections?.guias_mayores?.active ??
        (club?.type === "guias_mayores" || false),
      meetingDay: club?.meetingDay || "",
      meetingTime: club?.meetingTime || "",
      contactEmail: club?.contactEmail || "",
      contactPhone: club?.contactPhone || "",
    },
  });

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error || "Archivo inválido");
      return;
    }

    // Materializar en memoria: en móvil el File es solo una referencia que el
    // SO puede invalidar antes del submit (NotReadableError al subir).
    let safeFile = file;
    try {
      const buffer = await file.arrayBuffer();
      safeFile = new File([buffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch (err) {
      console.error("No se pudo leer el archivo seleccionado:", err);
      toast.error("No se pudo leer el archivo. Vuelve a seleccionarlo.");
      return;
    }

    setLogoFile(safeFile);
    setRemoveLogo(false);

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(safeFile);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: ClubForm) => {
    if (isFusion && fusionOf.length < 2) {
      toast.error("Un club fusión necesita al menos 2 clubes de origen.");
      return;
    }
    // Quitar la marca de fusión (o recortar sus orígenes) con miembros dentro
    // dejaría membresías aditivas huérfanas sin camino de disolución.
    if (isEditing && club?.isFusion && (club.memberCount ?? 0) > 0) {
      const removedOrigin = (club.fusionOf || []).some(
        (id) => !fusionOf.includes(id),
      );
      if (!isFusion || removedOrigin) {
        toast.error(
          "Esta fusión tiene miembros. Disuélvela primero desde su página de detalle.",
        );
        return;
      }
    }
    try {
      let logoUrl = club?.logoUrl;
      let logoPath = club?.logoPath;

      // Subir nuevo logo si hay archivo
      if (logoFile) {
        setUploadingLogo(true);
        try {
          // Si hay logo anterior, eliminarlo
          if (club?.logoPath) {
            try {
              await deleteImage(club.logoPath);
            } catch (e) {
              console.warn("No se pudo eliminar logo anterior:", e);
            }
          }

          const clubId = isEditing ? club.id : `temp_${Date.now()}`;
          const result = await uploadClubLogo(logoFile, clubId);
          logoUrl = result.url;
          logoPath = result.path;
        } catch (error) {
          console.error("Error subiendo logo:", error);
          toast.error("Error al subir el logo");
          setUploadingLogo(false);
          return;
        }
        setUploadingLogo(false);
      } else if (removeLogo && club?.logoPath) {
        // Eliminar logo existente
        try {
          await deleteImage(club.logoPath);
        } catch (e) {
          console.warn("No se pudo eliminar logo:", e);
        }
        logoUrl = undefined;
        logoPath = undefined;
      }

      if (isEditing) {
        // OJO: hay que CONSERVAR lo que ya trae cada tipo de club
        // (directorId / directorUserId / directorName). Antes se reescribía
        // el mapa entero con solo `active`, así que cualquier edición del club
        // —cambiar la iglesia, el horario, el logo— borraba a sus directores
        // de tipo: seguían con permisos pero desaparecían del club, y su tipo
        // volvía a aparecer como "libre" en el registro.
        const keep: Partial<Record<ClubType, SectionConfig>> =
          club?.sections ?? {};
        const sections = {
          ...keep,
          aventureros: {
            ...(keep.aventureros ?? {}),
            active: data.sectionAventureros,
          },
          conquistadores: {
            ...(keep.conquistadores ?? {}),
            active: data.sectionConquistadores,
          },
          guias_mayores: {
            ...(keep.guias_mayores ?? {}),
            active: data.sectionGuiasMayores,
          },
        };
        // Resolver iglesia: usar selección explícita o, si el usuario escribió
        // el nombre sin clickear el dropdown, intentar match exacto.
        let resolvedChurchId = selectedChurchId;
        if (!resolvedChurchId && churchSearch.trim()) {
          const q = churchSearch.trim().toLowerCase();
          const exact = availableChurches.find(
            (c) => c.name.toLowerCase() === q,
          );
          if (exact) resolvedChurchId = exact.id;
        }
        const selectedChurchDoc = availableChurches.find(
          (c) => c.id === resolvedChurchId,
        );
        if (churchSearch.trim() && !selectedChurchDoc) {
          toast.error(
            "Selecciona una iglesia de la lista (no se pudo identificar la iglesia escrita).",
          );
          return;
        }
        await updateMutation.mutateAsync({
          id: club.id,
          data: {
            name: data.name,
            churchId: resolvedChurchId || undefined,
            churchName: selectedChurchDoc?.name,
            meetingDay: data.meetingDay,
            meetingTime: data.meetingTime,
            contactEmail: data.contactEmail,
            contactPhone: data.contactPhone,
            sections,
            logoUrl,
            logoPath,
            isFusion,
            fusionOf: isFusion ? fusionOf : [],
          },
        });
      } else {
        const sections = {
          aventureros: { active: data.sectionAventureros },
          conquistadores: { active: data.sectionConquistadores },
          guias_mayores: { active: data.sectionGuiasMayores },
        };
        // Legacy type: first active section for backward compat
        const firstActive = data.sectionAventureros
          ? "aventureros"
          : data.sectionConquistadores
            ? "conquistadores"
            : "guias_mayores";
        let resolvedChurchId = selectedChurchId || churchId || "";
        if (!resolvedChurchId && churchSearch.trim()) {
          const q = churchSearch.trim().toLowerCase();
          const exact = availableChurches.find(
            (c) => c.name.toLowerCase() === q,
          );
          if (exact) resolvedChurchId = exact.id;
        }
        const selectedChurchDoc = availableChurches.find(
          (c) => c.id === resolvedChurchId,
        );
        if (churchSearch.trim() && !selectedChurchDoc) {
          toast.error(
            "Selecciona una iglesia de la lista (no se pudo identificar la iglesia escrita).",
          );
          return;
        }
        await createMutation.mutateAsync({
          name: data.name,
          meetingDay: data.meetingDay,
          meetingTime: data.meetingTime,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          sections,
          type: firstActive,
          ...(resolvedChurchId ? { churchId: resolvedChurchId } : {}),
          ...(selectedChurchDoc?.name
            ? { churchName: selectedChurchDoc.name }
            : {}),
          status: "active",
          logoUrl,
          logoPath,
          ...(isFusion ? { isFusion: true, fusionOf } : {}),
        } as any);
      }
      onClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const isLoading =
    createMutation.isPending || updateMutation.isPending || uploadingLogo;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Club" : "Nuevo Club"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Logo del Club */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo del Club
            </label>
            <div className="flex items-center gap-4">
              {/* Preview */}
              <div className="relative">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo del club"
                    className="w-20 h-20 rounded-xl object-cover border-2 border-gray-200 shadow-sm"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md"
                    title="Eliminar logo"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Upload button */}
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleLogoSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {logoPreview ? "Cambiar logo" : "Subir logo"}
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, WebP o GIF. Máx 5MB
                </p>
              </div>
            </div>
          </div>

          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Centinelas"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Iglesia
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar iglesia..."
                value={churchSearch}
                onChange={(e) => {
                  setChurchSearch(e.target.value);
                  if (!e.target.value) {
                    setSelectedChurchId("");
                  }
                }}
                className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 pl-9 text-sm focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              {selectedChurchId && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedChurchId("");
                    setChurchSearch("");
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Quitar iglesia"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {churchSearch.length >= 2 &&
              !selectedChurchId &&
              (() => {
                const q = churchSearch.toLowerCase();
                const matches = availableChurches
                  .filter((c) => {
                    const district = (getDistrictLabel(c) || "").toLowerCase();
                    return (
                      c.name.toLowerCase().includes(q) || district.includes(q)
                    );
                  })
                  .slice(0, 8);
                return matches.length > 0 ? (
                  <ul className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden max-h-44 overflow-y-auto shadow-sm">
                    {matches.map((c) => {
                      const districtLabel = getDistrictLabel(c);
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedChurchId(c.id);
                              setChurchSearch(c.name);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 text-gray-800"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate">{c.name}</span>
                              {districtLabel && (
                                <span className="text-xs text-gray-500 shrink-0">
                                  {districtLabel}
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Sin resultados para "{churchSearch}"
                  </p>
                );
              })()}
            {selectedChurch && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                {selectedChurch.name}
                {getDistrictLabel(selectedChurch)
                  ? ` · ${getDistrictLabel(selectedChurch)}`
                  : ""}
              </p>
            )}
            {availableChurches.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No hay iglesias disponibles
                {associationId ? " en esta asociación" : ""}.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secciones del Club *
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("sectionAventureros")}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Aventureros (6-9 años)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("sectionConquistadores")}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm">Conquistadores (10-15 años)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("sectionGuiasMayores")}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm">Guías Mayores (16+ años)</span>
              </label>
            </div>
          </div>

          {/* F11: Club fusión */}
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isFusion}
                onChange={(e) => setIsFusion(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm font-medium">
                Club fusión (temporal, para un evento)
              </span>
            </label>
            {isFusion && (
              <>
                <p className="text-xs text-amber-800">
                  Une 2 o más clubes reales para participar juntos en un evento.
                  Los miembros conservan su club de origen y al disolver la
                  fusión vuelven a él automáticamente.
                </p>
                <div className="max-h-36 overflow-y-auto space-y-1 bg-white rounded-lg border border-amber-200 p-2">
                  {fusionCandidates.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={fusionOf.includes(c.id)}
                        onChange={(e) =>
                          setFusionOf((prev) =>
                            e.target.checked
                              ? [...prev, c.id]
                              : prev.filter((id) => id !== c.id),
                          )
                        }
                        className="w-4 h-4 rounded border-gray-300 text-amber-600"
                      />
                      <span className="truncate">{c.name}</span>
                    </label>
                  ))}
                  {fusionCandidates.length === 0 && (
                    <p className="text-xs text-gray-500">
                      No hay clubes activos para fusionar.
                    </p>
                  )}
                </div>
                {fusionOf.length < 2 && (
                  <p className="text-xs text-red-600">
                    Selecciona al menos 2 clubes de origen.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Día de reunión
              </label>
              <select
                {...register("meetingDay")}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              >
                <option value="">Seleccionar</option>
                <option value="domingo">Domingo</option>
                <option value="lunes">Lunes</option>
                <option value="martes">Martes</option>
                <option value="miercoles">Miércoles</option>
                <option value="jueves">Jueves</option>
                <option value="viernes">Viernes</option>
                <option value="sabado">Sábado</option>
              </select>
            </div>

            <Input
              label="Hora de reunión"
              type="time"
              {...register("meetingTime")}
              error={errors.meetingTime?.message}
            />
          </div>

          <Input
            label="Email de contacto"
            type="email"
            {...register("contactEmail")}
            error={errors.contactEmail?.message}
            placeholder="club@iglesia.org"
          />

          <Input
            label="Teléfono de contacto"
            {...register("contactPhone")}
            error={errors.contactPhone?.message}
            placeholder="+57 300 123 4567"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
